import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { JobindsatsElement } from '../core/jobindsats-element';
import { TabOption } from '../shared/ji-tab-element';
import { ElementPeriodDto } from '@jobindsats/typescript-fetch-jobindsats';
import {getDisplayNameFromPeriodType, PeriodTab} from "../util/period-type-parser";


@customElement('maaling-period-selection-element')
export class MaalingPeriodSelectionElement extends JobindsatsElement(LitElement) {
    static styles = css`
        .period-table {
            border: thin solid var(--main-blue-25);
            table-layout: fixed;
            border-spacing: 0;
            width: 100%;
        }
        .select-single-checkbox {
            width: 20px;
            height: 20px;
        }
        .select-all-checkbox {
            width: 20px;
            height: 20px;
        }
        .period-container {
            padding: 20px 30px 30px;
        }
        .period-column {
            font-weight: normal;
            text-align: center;
            cursor: default;
            user-select: none;
            padding-top: 20px;
        }
        .period-selection-column {
            text-align: center;
            border-bottom: thin solid var(--main-blue-25);
            padding-top: 5px;
            padding-bottom: 10px;
        }
        .period-selection-row {
            border-right: thin solid var(--main-blue-25);
            text-align: center
        }
        .period-row-header {
            max-width: 50px;
            cursor: default;
            user-select: none;
            text-align: end;
        }
        .period-cell {
            text-align: center;
            padding-top: 3px;
            padding-bottom: 3px;
        }
        .tab-period-extension {
            display: block;
        }
        .dropdown-period-extension {
            display: none;
            padding: 20px 24px 15px;
        }
        input {
            cursor: pointer;
        }
        .zebra {
            background: var(--main-zebra);
        }
        .select-all-cell{
            border-bottom: thin solid var(--main-blue-25);
            border-right: thin solid var(--main-blue-25);
        }
        .scroll {
            overflow-y: auto;
        }
        @media only screen and (max-width:  1060px) {
            .tab-period-extension {
                display: none;
            }
            .dropdown-period-extension {
                display: block;
            }
            .period-container {
                padding-left: 20px;
                padding-right: 20px;
                padding-top: 10px;
                padding-bottom: 40px;
            }
        }
    `;

    months = ['Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'December'];
    ytds = ['Januar', 'Jan-feb', 'Jan-mar', 'Jan-apr', 'Jan-maj', 'Jan-jun', 'Jan-jul', 'Jan-aug', 'Jan-sep', 'Jan-okt', 'Jan-nov', 'Jan-dec'];
    quarters = ['1. Kvartal', '2. Kvartal', '3. Kvartal', '4. Kvartal'];
    rollingYears = ['2. kvartal - 1. kvartal', '3. kvartal - 2. kvartal', '4. kvartal - 3. kvartal', '1. kvartal - 4. kvartal'];
    years = ['Hele året'];
    @property()
    periodConfiguration?: ElementPeriodDto[];

    @state()
    selectedPeriodType?: string;

    periodsMap: Map<string, [string[], number[]]> = new Map();

    @state()
    selectedPeriods: Map<string, boolean> = new Map();

    constructor() {
        super();
    }

    render() {
        if (this.periodConfiguration && this.periodConfiguration.length > 0) {
            const periodTypes = Array.from(this.periodConfiguration.reduce((prev, cur) => {
                if (cur.periodType) {
                    prev.add(cur.periodType.trim());
                }
                return prev;
            }, new Set<string>()).values() ?? []);
            if (!this.selectedPeriodType) {
                this.selectedPeriodType = periodTypes[0];
            }
            const selectedPeriod = this.selectedPeriodType;

            const periodOptions: TabOption[] = periodTypes.map((type, index) => ({
                key: type,
                selected: type === selectedPeriod,
                tabName: getDisplayNameFromPeriodType(type),
            }));
            const yearRange = Array.from(this.periodConfiguration.reduce((prev, cur) => {
                if (cur.periodType && cur.periodType.trim() === selectedPeriod && cur.year !== undefined && cur.year !== null) {
                    prev.add(cur.year);
                }
                return prev;
            }, new Set<number>()).values() ?? []);
            yearRange.sort();
            const rows = yearRange.map(year => year.toString());
            var columnNames = this.getColumnArrayFromPeriodType(selectedPeriod);
            var neededColumns: string[] = [];
            for (var i = 0; i < yearRange.length; i++) {
                const columnIndices = this.periodConfiguration.reduce<number[]>((prev, cur) => {
                    if (cur.taller && cur.periodType && cur.periodType.trim() === selectedPeriod && cur.year === yearRange[i]) {
                        prev.push(cur.taller-1);
                    }
                    return prev;
                }, []) ?? [];
                
                const columnNameYear = columnIndices.map(columnIndex => columnNames[columnIndex]);
                this.periodsMap.set(rows[i], [columnNameYear, columnIndices]);

                neededColumns =  [...new Set(neededColumns.concat(columnNameYear))];
            }

            columnNames = columnNames.filter(col => neededColumns.includes(col))

            return html`
                <ji-dropdown-element
                    expanded="true"
                    title="Periode"
                    class="dropdown-period-extension"
                    .options=${periodOptions}
                    selected-period=${selectedPeriod}
                    @option-selected=${this.handleTabSelection}></ji-dropdown-element>
                <ji-tab-element class="tab-period-extension" databank="true" .options=${periodOptions} @tab-selected=${this.handleTabSelection}></ji-tab-element>
                <div class="scroll thin-scrollbar">
                    <div class="period-container" style="width: ${179 + (columnNames.length * 92) + 'px'}">
                        <table class="period-table">
                            <thead>
                                <tr>
                                    <th class="select-all-cell" rowspan=2 colspan=2>
                                            <a href="#!" role="button" @click=${(ev: Event) => { ev.preventDefault(); this.handleAllSelected(rows, columnNames); }}>Vælg / Fravælg alle</a>
                                    </th>
                                    ${columnNames.map(column => html`
                                        <th class="period-column" scope="col" @click=${() => { this.handlePeriodColumnSelection(column, rows); }}>${column}</th>`)}
                                </tr>
                                <tr>
                                    ${columnNames.map((column) => html`
                                        <td class="period-selection-column" scope="col" @click=${() => { this.handlePeriodColumnSelection(column, rows); }}>
                                            <input
                                                aria-label="Vælg/Fravælg alle i kolonnen"
                                                class="select-all-checkbox"
                                                type="checkbox"
                                                id=${ column }>
                                        </td>`)}
                                </tr>
                            </thead>
                            <tbody>
                                ${rows.map((row, idx) => html`
                                    <tr class="${idx % 2 ? 'zebra' : ''}">
                                        <th class="period-row-header" scope="row" @click=${() => { this.handlePeriodRowSelection(row, rows, columnNames); }}>${row}</th>
                                        ${this.showSelectRowColumn(columnNames) ? html`
                                            <td class="period-selection-row" @click=${() => { this.handlePeriodRowSelection(row, rows, columnNames); }}>
                                                <input
                                                    aria-label="Vælg/Fravælg alle i rækken"
                                                    class="select-all-checkbox"
                                                    type="checkbox"
                                                    id=${ row }>
                                            </td>` : undefined}
                                        ${columnNames.map((column) => (this.periodsMap.get(row))?.[0].includes(column) ? html`
                                            ${columnNames.length === 1 ?
                                                html`<td style="border-right: thin solid var(--main-blue-25);"></td>` : undefined}
                                            <td class="period-cell" @click=${() => this.handlePeriodSelection(column, row, rows)}>
                                                <input aria-label="Vælg/Fravælg denne"
                                                    class="select-single-checkbox"
                                                    .checked=${this.selectedPeriods.get(`${column},${row}`) === true}
                                                    type="checkbox">
                                            </td>` : html`<td></td>`)}
                                    </tr>`)}
                            </tbody>
                        </table>
                    </div>
                </div>`;
            } else {
                return html`
                    <div class="period-container">
                        Ingen perioder tilknyttet måling
                    </div>`;
        }
    }



    private handleAllSelected(rows:string[], cols:string[]) {
        if (Array.from(this.selectedPeriods.values()).some(period => period)) {
            // should remove all checkmarks if just one is selected
            rows.forEach(row => {
                var [columns, _] = this.periodsMap.get(row)!;
                columns.forEach(col => {
                    this.selectedPeriods.set(`${col},${row}`, false);
                    this.ensureColumnToggleState(col, rows);
                    this.ensureRowToggleState(row);
                })
            });
        } else {
            rows.forEach(row => {
                var [columns, _] = this.periodsMap.get(row)!;
                columns.forEach(col => {
                    this.selectedPeriods.set(`${col},${row}`, true);
                    this.ensureColumnToggleState(col, rows);
                    this.ensureRowToggleState(row);
                })
            });
        }
        this.requestUpdate();
        this.fireSelectionEvent();
    }

    private showSelectRowColumn(columns: string[]) {
        return columns.length > 1;
    }

    private handlePeriodSelection(column: string, row: string, rows: string[]) {
        const periodSelection = this.selectedPeriods.get(`${column},${row}`);
        this.selectedPeriods.set(`${column},${row}`, !periodSelection);
        this.requestUpdate();
        this.fireSelectionEvent();
        this.ensureColumnToggleState(column, rows);
        this.ensureRowToggleState(row);
    }

    private handlePeriodRowSelection(row: string, rows: string[], columns: string[]) {
        const rowSelection = (this.shadowRoot!.getElementById(row) as HTMLInputElement).checked;
        columns.forEach(column => {
            this.selectedPeriods.set(`${column},${row}`, rowSelection);
            this.ensureColumnToggleState(column, rows);
        });
        this.ensureRowToggleState(row);
        this.requestUpdate();
        this.fireSelectionEvent();
    }

    private handlePeriodColumnSelection(column: string, rows: string[]) {
        const columnSelection = (this.shadowRoot!.getElementById(column) as HTMLInputElement).checked;
        rows.forEach(row => {
            var [columnsForRow, _] = this.periodsMap.get(row)!;
            if (columnsForRow.includes(column)) {
                this.selectedPeriods.set(`${column},${row}`, columnSelection);
                this.ensureRowToggleState(row);
            }
        });
        this.ensureColumnToggleState(column, rows);
        this.requestUpdate();
        this.fireSelectionEvent();
    }

    private fireSelectionEvent() {
        const periods: string[] = [];
        this.selectedPeriods.forEach((selected, periodKey) => {
            if (selected && periodKey.includes(',')) {
                const relevantMonth = periodKey.split(',')[0];
                const relevantYear = periodKey.split(',')[1];
                const monthAndIndices = this.periodsMap.get(relevantYear)!;
                const months = monthAndIndices[0];
                const indices =  monthAndIndices[1];
                const taller = monthAndIndices ? indices[months.findIndex(month => month === relevantMonth)]+1 : '';
                if (taller) {
                    periods.push(relevantYear+this.selectedPeriodType+('0' + taller).slice(-2));
                }
            }
        });

        const periodSelectionEvent = new CustomEvent("period-selected",
            {
                detail: {
                    periods,
                },
                composed: true,
                bubbles: true,
            }
        );
        this.dispatchEvent(periodSelectionEvent);
    }

    private getColumnArrayFromPeriodType(periodeType: string): string[] {
        switch (periodeType) {
            case PeriodTab.MONTH:
                return this.months;
            case PeriodTab.QUARTER:
                return this.quarters;
            case PeriodTab.YEAR:
                return this.years;
            case PeriodTab.ROLLYINGYEAR:
                return this.rollingYears;
            case PeriodTab.MONTHYTD:
                return this.ytds;
            default:
                return [periodeType];
        }
    }

    private handleTabSelection(ev: CustomEvent) {
        // Update data
        const selectedTab = ev.detail.key as PeriodTab;
        this.selectedPeriodType = selectedTab;
        this.selectedPeriods = new Map();
        this.fireSelectionEvent();
        // Update gui
        const allCheckboxes = this.shadowRoot?.querySelectorAll('input');
        if (!!allCheckboxes){
            allCheckboxes.forEach(box => {
                box.checked = false;
                box.indeterminate = false;
            });
        }
    }

    private ensureRowToggleState(row: string) {
        var hasAnyPeriodToggledOn = this.checkIfRowHasAnyPeriodToggledOn(row);
        var hasAnyPeriodToggledOff = this.checkIfRowHasAnyPeriodToggledOff(row);

        const checkbox = this.shadowRoot?.getElementById(row) as HTMLInputElement;
        if (!!checkbox) {
            if (hasAnyPeriodToggledOn && hasAnyPeriodToggledOff) {
                checkbox.indeterminate = true;
            } else if (!hasAnyPeriodToggledOff) {
                checkbox.indeterminate = false;
                checkbox.checked = true;
            } else if (!hasAnyPeriodToggledOn) {
                checkbox.indeterminate = false;
                checkbox.checked = false;
            }
        }
    }

    private ensureColumnToggleState(column: string, rows: string[]) {
        var hasAnyPeriodToggledOn = this.checkIfColumnHasAnyPeriodToggledOn(column);
        var hasAnyPeriodToggledOff = this.checkIfColumnHasAnyPeriodToggledOff(column, rows);

        const checkbox = this.shadowRoot?.getElementById(column) as HTMLInputElement;
        if (!!checkbox) {
            if (hasAnyPeriodToggledOn && hasAnyPeriodToggledOff) {
                checkbox.indeterminate = true;
            } else if (!hasAnyPeriodToggledOff) {
                checkbox.indeterminate = false;
                checkbox.checked = true;
            } else if (!hasAnyPeriodToggledOn) {
                checkbox.indeterminate = false;
                checkbox.checked = false;
            }
        }
    }

    private checkIfRowHasAnyPeriodToggledOn(row: string) {
        for (let key of this.selectedPeriods.keys()) {
            // Key includes row, and toggle is on.
            if (key.includes(row) && !!this.selectedPeriods.get(key)) {
                return true;
            }
        }
        return false;
    }

    private checkIfRowHasAnyPeriodToggledOff(row: string) {
        var [columnsForRow, _] = this.periodsMap.get(row)!;
        for (let column of columnsForRow) {
            var toggleState = this.selectedPeriods.get(`${column},${row}`);
            // Undefined/Missing key corresponds to toggled off
            if (toggleState === undefined || toggleState === false) {
                return true;
            }
        }
        return false;
    }

    private checkIfColumnHasAnyPeriodToggledOn(column: string) {
        for (let key of this.selectedPeriods.keys()) {
            // Key includes column, and toggle is on.
            if (key.includes(column) && !!this.selectedPeriods.get(key)) {
                return true;
            }
        }
        return false;
    }

    private checkIfColumnHasAnyPeriodToggledOff(column: string, rows: string[]) {
        for (let row of rows) {
            var [columnsForRow, _] = this.periodsMap.get(row)!;
            if (columnsForRow.includes(column)) {
                var toggleState = this.selectedPeriods.get(`${column},${row}`);
                // Undefined/Missing key corresponds to toggled off
                if (toggleState === undefined || toggleState === false) {
                    return true;
                }
            }
        }
        return false;
    }
}